/**
 * Coluna esquerda: a fila de atendimento (H04, §11.2).
 *
 * ── O que a diferencia da caixa de entrada do Chatwoot ────────────────────
 *
 * O contexto comercial na linha: etapa e valor da oportunidade, ao lado da
 * prévia da mensagem. É o que permite ao vendedor escolher por onde começar sem
 * abrir uma por uma — e é a razão de a D02 servir esta lista dos read models, e
 * não da engine.
 *
 * ── Não lida é trilho, não fundo ─────────────────────────────────
 *
 * Fundo em lista longa cansa e briga com o estado de seleção: a linha
 * selecionada E não lida teria duas cores disputando. O trilho fino na borda
 * esquerda sobrevive aos dois, e é o que o sistema visual fixou para ênfase.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  type WheelEvent,
} from 'react';
import type { ItemDaInbox, PaginaDaInbox } from '@cliqcrm/contracts';
import { Etiquetas } from '@/entities/atendimento/etiqueta';
import { Estado } from '@/shared/ui/estados/estados';
import { Defasado } from '@/shared/ui/estados/estados';
import {
  Avatar,
  Botao,
  Campo,
  Chip,
  FiltroPilula,
  Icone,
  Lista as ListaComum,
  ListaLinha,
  NaoLidas,
  type NomeDoIcone,
} from '@/shared/ui';
import { dinheiro, horaCurta, idadeCurta, nomeParaExibir } from '@/shared/lib/formato';
import { iconeDoCanal, nomeDoCanal } from '@/entities/atendimento/canal';
import { NOMES_DE_PRIORIDADE, NOMES_DE_SITUACAO } from '@/entities/atendimento/conversa';
import { textoSemMarcadores } from '../lib/formatacao';
import { useCaixasDoAtendimento } from '@/shared/api/consultas';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { ControleDeNotificacoes } from '@/widgets/notificacoes';
import { usePreferenciasDeNotificacao } from '@/widgets/notificacoes';

export interface FiltrosDaInbox {
  readonly busca: string;
  /**
   * As caixas escolhidas, separadas por vírgula — o formato do parâmetro
   * `caixa` da lista, que aceita várias. Vazio = todas as do escopo.
   */
  readonly caixa: string;
  readonly situacao: string;
  readonly canal: string;
  readonly prioridade: string;
  readonly responsavel: string;
}

export const FILTROS_VAZIOS: FiltrosDaInbox = {
  busca: '',
  caixa: '',
  situacao: '',
  canal: '',
  prioridade: '',
  responsavel: '',
};

export function temFiltroAtivo(filtros: FiltrosDaInbox): boolean {
  return Object.values(filtros).some((valor) => valor !== '');
}

export function Lista({
  consulta,
  filtros,
  aoFiltrar,
  selecionada,
  aoSelecionar,
}: {
  consulta: Parameters<typeof Estado<PaginaDaInbox>>[0]['consulta'];
  filtros: FiltrosDaInbox;
  aoFiltrar: (filtros: FiltrosDaInbox) => void;
  selecionada: string | null;
  aoSelecionar: (id: string) => void;
}): ReactNode {
  const filtrando = temFiltroAtivo(filtros);

  /*
   * Situação, prioridade e responsável moram num painel que abre do botão de
   * filtros, ao lado da busca.
   *
   * ── Por que recolher algo que já funcionava ───────────────────────────
   *
   * Porque eles ocupavam linhas fixas no topo de uma coluna estreita, e o que
   * essa coluna precisa mostrar é CONVERSA. Em tela de notebook isso custava
   * duas conversas visíveis, o dia inteiro, para um controle que se usa
   * algumas vezes por dia.
   *
   * ── E o filtro ligado e escondido ─────────────────────────────────────
   *
   * É a pior combinação possível: a lista mostra menos do que existe e nada na
   * tela explica por quê. Quem chega numa estação já filtrada por outra pessoa
   * conclui que não há conversa.
   *
   * Até aqui isso se resolvia abrindo os seletores sozinhos quando havia
   * filtro. Agora quem conta são os chips abaixo da busca — um por filtro, e
   * com o painel fechado —, mais o número no botão. Abrir por conta própria
   * cobriria a lista com um painel que ninguém pediu. Então nasce fechado.
   */
  const [abertos, definirAbertos] = useState(false);
  const aplicados = filtrosAplicados(filtros);

  /*
   * O painel de notificações (#536). Fechado de início: é ajuste que se faz uma
   * vez, e não deve disputar espaço com a lista. O sino riscado no botão conta,
   * com o painel fechado, que alguma coisa está desligada.
   */
  const [notificacoesAbertas, definirNotificacoesAbertas] = useState(false);
  const preferencias = usePreferenciasDeNotificacao();
  const algumaDesligada = !preferencias.naTela || !preferencias.foraDaTela;

  return (
    <section className="col col--lista" aria-label="Conversas">
      <header className="col__cabecalho">
        <h2>Conversas</h2>
        {/*
          O total saiu do cabeçalho.
 
          Ele dizia o mesmo que a aba "Todos" logo abaixo passou a dizer, e com
          uma diferença ruim: com uma aba escolhida, os dois números discordam —
          um é o do recorte, o outro é o do universo. Dois números próximos e
          diferentes na mesma tela fazem quem lê parar para decidir em qual
          acreditar.
        */}
        {/*
          O funil saiu daqui e foi para o lado da busca: ele abre um painel que
          se ancora na busca, e o número de filtros ativos fica junto do campo
          que eles recortam. O sino ficou sozinho no canto.
        */}
        <button
          className={`filtros__botao${algumaDesligada ? ' filtros__botao--ativo' : ''}`}
          type="button"
          aria-expanded={notificacoesAbertas}
          aria-controls="notificacoes-da-tela"
          aria-label={algumaDesligada ? 'Notificações — há aviso desligado' : 'Notificações'}
          title={algumaDesligada ? 'Notificações — há aviso desligado' : 'Notificações'}
          onClick={() => definirNotificacoesAbertas((antes) => !antes)}
        >
          <Icone nome="sino" />
          {algumaDesligada && <span className="filtros__ponto" aria-hidden="true" />}
        </button>
      </header>

      <ControleDeNotificacoes id="notificacoes-da-tela" aberto={notificacoesAbertas} />

      {/*
        "Minhas", "Não atribuídas" e "Todas" moraram aqui como pílulas. Desde
        25/10 vivem só no painel do funil, no grupo "Responsável", com as mesmas
        contagens: a coluna ganha a altura de uma linha de conversa, e o recorte
        escolhido continua à vista nos filtros aplicados, embaixo da busca.
      */}
      <div className="filtros">
        <BuscaComFiltros
          filtros={filtros}
          aoFiltrar={aoFiltrar}
          contagens={consulta.data?.abas}
          abertos={abertos}
          definirAbertos={definirAbertos}
          quantos={aplicados.length}
        />

        {/*
          Os filtros do painel, à vista com ele fechado: é o que conta que a
          lista está recortada. Cada chip tira só o seu; "Limpar tudo" volta a
          lista inteira, busca e caixas inclusive — é o que o antigo "Limpar
          filtros" fazia.
        */}
        {aplicados.length > 0 && (
          <div className="filtros__aplicados" role="group" aria-label="Filtros aplicados">
            {aplicados.map((aplicado) => (
              <button
                key={aplicado.campo}
                className="filtros__aplicado"
                type="button"
                aria-label={`Remover o filtro ${aplicado.grupo}: ${aplicado.nome}`}
                title="Remover este filtro"
                onClick={() => aoFiltrar({ ...filtros, [aplicado.campo]: '' })}
              >
                <span className="filtros__aplicado-grupo">{aplicado.grupo}:</span>
                {aplicado.nome}
                <Icone nome="fechar" />
              </button>
            ))}
            <button
              className="filtros__limpar"
              type="button"
              onClick={() => aoFiltrar(FILTROS_VAZIOS)}
            >
              Limpar tudo
            </button>
          </div>
        )}

        <FiltroDeCaixas
          escolhidas={filtros.caixa}
          aoMudar={(caixa) => aoFiltrar({ ...filtros, caixa })}
        />
      </div>

      {consulta.data?.frescor.stale && (
        <Defasado
          projetadoEm={consulta.data.frescor.projetado_em}
          origem="Esta lista"
          aoAtualizar={() => void consulta.refetch()}
        />
      )}

      <div className="col__rolagem">
        <Estado
          consulta={consulta}
          forma="lista"
          linhas={6}
          vazio={{
            quando: (pagina) => pagina.itens.length === 0,
            titulo: filtrando ? 'Nada com esses filtros' : 'Nenhuma conversa esperando',
            // A distinção é o ponto do estado vazio: fila limpa e filtro que
            // escondeu tudo parecem iguais e exigem ações opostas.
            porque: filtrando
              ? 'Nenhuma conversa do seu escopo bate com o que você filtrou. Limpe os filtros para ver o resto.'
              : 'Tudo respondido. Conversas novas aparecem aqui sozinhas, sem recarregar.',
          }}
        >
          {(pagina) => (
            <ListaComum>
              {pagina.itens.map((item) => (
                <LinhaDaConversa
                  key={item.conversa.id}
                  item={item}
                  selecionada={item.conversa.id === selecionada}
                  aoSelecionar={aoSelecionar}
                />
              ))}
            </ListaComum>
          )}
        </Estado>
      </div>
    </section>
  );
}

/**
 * "Caixas" (#463): ver só uma caixa de entrada, ou algumas.
 *
 * Pílulas, e não um seletor: a escolha é MÚLTIPLA — quem atende dois números
 * de três quer os dois juntos —, e o `<select multiple>` nativo esconde o que
 * está marcado atrás de Ctrl+clique. Nenhuma ligada é "todas", como os outros
 * filtros vazios.
 *
 * Com menos de duas caixas o grupo não aparece: não há o que separar.
 */
function FiltroDeCaixas({
  escolhidas,
  aoMudar,
}: {
  escolhidas: string;
  aoMudar: (caixas: string) => void;
}): ReactNode {
  const consulta = useCaixasDoAtendimento();
  const caixas = consulta.data?.itens ?? [];
  if (caixas.length < 2) return null;

  const ligadas = new Set(escolhidas.split(',').filter(Boolean));
  const alternar = (id: string): void => {
    const proximas = new Set(ligadas);
    if (proximas.has(id)) proximas.delete(id);
    else proximas.add(id);
    // Na ordem da lista, e não na do clique: a mesma escolha vira a mesma
    // chave de consulta, e o cache não se divide à toa.
    aoMudar(
      caixas
        .map((c) => c.referencia_externa)
        .filter((c) => proximas.has(c))
        .join(','),
    );
  };

  /*
   * Uma linha só, que rola de lado, em vez de quebrar: com três caixas de nome
   * comprido a fileira ia a duas linhas e empurrava a lista para baixo. O ícone
   * do canal vem antes do nome porque duas caixas podem ter nomes parecidos e
   * canais diferentes.
   */
  return (
    <div
      className="filtros__caixas"
      role="group"
      aria-label="Caixas de entrada"
      onWheel={rolarDeLado}
    >
      {caixas.map((caixa) => {
        const nome = caixa.nome ?? `Caixa ${caixa.referencia_externa}`;
        const canal = nomeDoCanal(caixa.canal);
        return (
          <FiltroPilula
            key={caixa.referencia_externa}
            miuda
            ligado={ligadas.has(caixa.referencia_externa)}
            aoAlternar={() => alternar(caixa.referencia_externa)}
            title={canal ? `${nome} (${canal})` : nome}
          >
            <span className="filtros__caixa-icone" aria-hidden="true">
              <Icone nome={ICONE_DO_CANAL[caixa.canal ?? ''] ?? 'atendimento'} />
            </span>
            <span className="filtros__caixa-nome">{nome}</span>
          </FiltroPilula>
        );
      })}
    </div>
  );
}

/**
 * O canal da caixa (C03) vira o ícone da família. Canal sem desenho cai no
 * balão do atendimento, como no logo da tela de Conexões.
 */
const ICONE_DO_CANAL: Readonly<Record<string, NomeDoIcone>> = {
  whatsapp_cloud: 'whatsapp',
  whatsapp_business: 'whatsapp',
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  facebook: 'facebook',
  web_widget: 'web-chat',
  webchat: 'web-chat',
  api: 'api',
  tiktok: 'tiktok',
};

/**
 * A roda do mouse rola a fileira de lado.
 *
 * Sem isto, quem não tem trackpad só alcança a última caixa pelo Tab: a roda
 * comum gira no eixo vertical, e a fileira só tem o horizontal. A barra de
 * rolagem fica escondida porque, no Windows, ela come 12px de altura de uma
 * faixa de 26.
 */
function rolarDeLado(evento: WheelEvent<HTMLDivElement>): void {
  if (Math.abs(evento.deltaY) <= Math.abs(evento.deltaX)) return;
  evento.currentTarget.scrollLeft += evento.deltaY;
}

/** Um filtro do painel que está ligado, do jeito que o chip o mostra. */
interface FiltroAplicado {
  readonly campo: 'situacao' | 'prioridade' | 'responsavel';
  readonly grupo: string;
  readonly nome: string;
}

/**
 * O que vira chip abaixo da busca, e o número no botão de filtros.
 *
 * Só situação e prioridade. O responsável também mora no painel, mas é o mesmo
 * campo das três abas, e a aba acesa já diz qual é: um chip "Minhas" embaixo
 * da aba "Minhas" acesa seria a mesma informação duas vezes, e faria a lista
 * pular ao trocar de aba. Busca e caixas aparecem nos próprios controles.
 */
function filtrosAplicados(filtros: FiltrosDaInbox): FiltroAplicado[] {
  const aplicados: FiltroAplicado[] = [];
  if (filtros.situacao) {
    aplicados.push({
      campo: 'situacao',
      grupo: 'Situação',
      nome: NOMES_DE_SITUACAO[filtros.situacao] ?? filtros.situacao,
    });
  }
  if (filtros.prioridade) {
    aplicados.push({
      campo: 'prioridade',
      grupo: 'Prioridade',
      nome: NOMES_DE_PRIORIDADE[filtros.prioridade] ?? filtros.prioridade,
    });
  }
  // Sem as pílulas, o recorte de responsável só fica à vista aqui.
  if (filtros.responsavel) {
    aplicados.push({
      campo: 'responsavel',
      grupo: 'Responsável',
      nome: NOMES_DE_RESPONSAVEL[filtros.responsavel] ?? 'Outra pessoa',
    });
  }
  return aplicados;
}

const OPCOES_DE_PRIORIDADE = [
  ['urgente', 'Urgente'],
  ['alta', 'Alta'],
  ['media', 'Média'],
  ['baixa', 'Baixa'],
] as const;

/*
 * `sem_responsavel` é valor do contrato, não ausência de filtro: é a fila que o
 * gestor precisa ver todo dia. `meus` é o mesmo valor da aba "Minhas".
 */
const OPCOES_DE_RESPONSAVEL = [
  ['meus', 'Minhas'],
  ['sem_responsavel', 'Não atribuídas'],
] as const;

const NOMES_DE_RESPONSAVEL: Readonly<Record<string, string>> =
  Object.fromEntries(OPCOES_DE_RESPONSAVEL);

/** Distância entre a busca e o painel, e o mínimo até a beirada da janela. */
const MARGEM = 4;
const BEIRADA = 8;

/**
 * A busca, o botão de filtros ao lado dela, e o painel que ele abre.
 *
 * ── O painel vai para a camada superior, como o `MenuAcoes` ───────────────
 *
 * No celular a coluna da lista tem `max-height: 40dvh` e `overflow: hidden`, e
 * um painel absoluto de 230px abaixo da busca sairia cortado ao meio — o
 * defeito que `tools/lint-css-recorte.mjs` existe para travar. O atributo
 * `popover` tira o painel do recorte sem tirá-lo do lugar na árvore: o clique
 * fora, a ordem do Tab (busca, botão, painel) e o foco continuam como se ele
 * estivesse ali.
 *
 * ── `hidden`, e não desmontar ─────────────────────────────────────────────
 *
 * Fechar pelo Esc devolve o foco ao botão. Desmontando, quem estava numa opção
 * perderia o foco para o nada e cairia no começo da página.
 *
 * ── Rolar a LISTA não fecha ───────────────────────────────────────────────
 *
 * O painel se ancora na busca, e a busca não rola: rola a lista embaixo dela.
 * Fechar a cada giro da roda sobre as conversas seria fechar o painel à toa.
 * Fecha só quando o que rola carrega a busca junto.
 */
function BuscaComFiltros({
  filtros,
  aoFiltrar,
  abertos,
  definirAbertos,
  quantos,
  contagens,
}: {
  filtros: FiltrosDaInbox;
  aoFiltrar: (filtros: FiltrosDaInbox) => void;
  abertos: boolean;
  definirAbertos: Dispatch<SetStateAction<boolean>>;
  quantos: number;
  /** Quantas conversas há em cada recorte de responsável, como as antigas pílulas mostravam. */
  contagens?: { readonly minhas?: number; readonly nao_atribuidas?: number } | undefined;
}): ReactNode {
  const raiz = useRef<HTMLDivElement>(null);
  const barra = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const [posicao, definirPosicao] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  /*
   * `useLayoutEffect`: entre pintar o painel no canto e movê-lo para baixo da
   * busca existe um quadro, e nele o painel aparece fora do lugar.
   */
  useLayoutEffect(() => {
    const caixa = painel.current;
    const ancora = barra.current?.getBoundingClientRect();
    if (!abertos || !caixa || !ancora) return;

    // Sem suporte a `popover`, o painel continua fixo na mesma posição — pior
    // só se um ancestral tiver `transform`, e nunca ausente.
    if (typeof caixa.showPopover === 'function' && !caixa.matches(':popover-open')) {
      try {
        caixa.showPopover();
      } catch {
        // Já aberto por outro caminho: a conta abaixo continua valendo.
      }
    }

    const top = ancora.bottom + MARGEM;
    definirPosicao({
      top,
      left: ancora.left,
      width: ancora.width,
      maxHeight: Math.max(0, window.innerHeight - BEIRADA - top),
    });

    return () => {
      if (typeof caixa.hidePopover === 'function' && caixa.matches(':popover-open')) {
        caixa.hidePopover();
      }
    };
  }, [abertos]);

  // Reabrir recalcula: posição velha faria o painel piscar no lugar de antes.
  useEffect(() => {
    if (!abertos) definirPosicao(null);
  }, [abertos]);

  /*
   * Fecha no clique fora, no Esc, ao redimensionar e quando a busca sai do
   * lugar. O Esc só devolve o foco ao botão se ele estava no painel: quem
   * aperta Esc no campo de busca está limpando o campo, e continua nele.
   */
  useEffect(() => {
    if (!abertos) return;
    const fechar = (): void => definirAbertos(false);
    const foraDele = (evento: MouseEvent): void => {
      if (!raiz.current?.contains(evento.target as Node)) fechar();
    };
    const noTeclado = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return;
      const estavaNoPainel = painel.current?.contains(document.activeElement) ?? false;
      fechar();
      if (estavaNoPainel) botao.current?.focus();
    };
    const aoRolar = (evento: Event): void => {
      const alvo = evento.target;
      if (alvo instanceof Node && barra.current && alvo.contains(barra.current)) fechar();
    };
    document.addEventListener('mousedown', foraDele);
    document.addEventListener('keydown', noTeclado);
    window.addEventListener('scroll', aoRolar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', foraDele);
      document.removeEventListener('keydown', noTeclado);
      window.removeEventListener('scroll', aoRolar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [abertos, definirAbertos]);

  /*
   * O nome acessível carrega o ESTADO, e não só o nome: "Filtros" sozinho não
   * diria que a lista está recortada, e é isso que o número conta a quem vê.
   */
  const nome =
    quantos === 0 ? 'Filtros' : quantos === 1 ? 'Filtros, 1 ativo' : `Filtros, ${quantos} ativos`;
  const algoNoPainel = quantos > 0 || filtros.responsavel !== '';

  return (
    <div
      className="filtros__busca"
      ref={raiz}
      // O Tab que sai do painel fecha: aberto e sem foco, ele cobriria as
      // primeiras conversas da lista sem ninguém usando.
      onBlur={(evento) => {
        const para = evento.relatedTarget;
        if (abertos && para instanceof Node && !raiz.current?.contains(para)) {
          definirAbertos(false);
        }
      }}
    >
      <div className="filtros__barra" ref={barra}>
        <div className="filtros__campo">
          <span className="filtros__lupa" aria-hidden="true">
            <Icone nome="buscar" />
          </span>
          <Campo
            type="search"
            rotuloOculto="Buscar conversa"
            value={filtros.busca}
            onChange={(e) => aoFiltrar({ ...filtros, busca: e.target.value })}
            placeholder="Buscar por nome ou mensagem"
          />
        </div>
        <button
          ref={botao}
          className={`filtros__abrir${quantos > 0 ? ' filtros__abrir--ativo' : ''}`}
          type="button"
          aria-expanded={abertos}
          aria-controls="filtros-da-inbox"
          aria-label={nome}
          title={nome}
          onClick={() => definirAbertos((antes) => !antes)}
        >
          {/*
            O funil e o número são desenho: quem usa leitor de tela ouve o nome
            do botão, que já diz os dois.
          */}
          <Icone nome="funil" />
          {quantos > 0 && (
            <span className="filtros__contador" aria-hidden="true">
              {quantos}
            </span>
          )}
        </button>
      </div>

      <div
        ref={painel}
        id="filtros-da-inbox"
        className="filtros__painel"
        role="group"
        aria-label="Filtros da lista"
        hidden={!abertos}
        popover="manual"
        /*
          Invisível até a conta terminar. Sem isto, o painel aparece no canto
          da tela por um quadro.
        */
        style={
          posicao
            ? {
                top: `${posicao.top}px`,
                left: `${posicao.left}px`,
                width: `${posicao.width}px`,
                maxHeight: `${posicao.maxHeight}px`,
              }
            : { top: 0, left: 0, visibility: 'hidden' }
        }
      >
        <div className="filtros__painel-topo">
          <span className="filtros__painel-titulo">Filtros</span>
          <Botao
            variante="fantasma"
            miudo
            desabilitado={!algoNoPainel}
            motivo="Nenhum filtro escolhido"
            onClick={() => aoFiltrar({ ...filtros, situacao: '', prioridade: '', responsavel: '' })}
          >
            Limpar
          </Botao>
        </div>
        <GrupoDeOpcoes
          rotulo="Situação"
          valor={filtros.situacao}
          opcoes={Object.entries(NOMES_DE_SITUACAO)}
          aoMudar={(v) => aoFiltrar({ ...filtros, situacao: v })}
        />
        <GrupoDeOpcoes
          rotulo="Prioridade"
          valor={filtros.prioridade}
          opcoes={OPCOES_DE_PRIORIDADE}
          aoMudar={(v) => aoFiltrar({ ...filtros, prioridade: v })}
        />
        <GrupoDeOpcoes
          rotulo="Responsável"
          valor={filtros.responsavel}
          opcoes={OPCOES_DE_RESPONSAVEL}
          contagens={{ meus: contagens?.minhas, sem_responsavel: contagens?.nao_atribuidas }}
          aoMudar={(v) => aoFiltrar({ ...filtros, responsavel: v })}
        />
      </div>
    </div>
  );
}

/**
 * Um filtro de escolha única, em pílulas: todas as opções à vista, a escolhida
 * em roxo cheio. Clicar na escolhida desliga — nenhuma ligada é "todas", como
 * nas caixas.
 *
 * Pílula, e não `<select>`: com quatro opções curtas, o menu nativo escondia
 * atrás de um clique o que cabe numa linha, e em três colunas de 89px cortava
 * "Responsável" antes da seta.
 */
function GrupoDeOpcoes({
  rotulo,
  valor,
  opcoes,
  contagens,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  opcoes: readonly (readonly [string, string])[];
  /** Quantas conversas cada opção tem, quando o servidor diz. */
  contagens?: Readonly<Record<string, number | undefined>>;
  aoMudar: (valor: string) => void;
}): ReactNode {
  const id = useId();
  return (
    <div className="filtros__grupo" role="group" aria-labelledby={id}>
      <span className="filtros__grupo-rotulo" id={id}>
        {rotulo}
      </span>
      <div className="filtros__opcoes">
        {opcoes.map(([chave, nome]) => (
          <FiltroPilula
            key={chave}
            miuda
            ligado={valor === chave}
            aoAlternar={() => aoMudar(valor === chave ? '' : chave)}
          >
            {nome}
            {contagens?.[chave] !== undefined && (
              <span className="filtros__opcao-contagem">{contagens[chave]}</span>
            )}
          </FiltroPilula>
        ))}
      </div>
    </div>
  );
}

function LinhaDaConversa({
  item,
  selecionada,
  aoSelecionar,
}: {
  item: ItemDaInbox;
  selecionada: boolean;
  aoSelecionar: (id: string) => void;
}): ReactNode {
  const { conversa, cliente, caixa, resumo_comercial: comercial, vinculo } = item;
  const nome = nomeParaExibir(cliente);
  const valor = dinheiro(comercial?.valor);

  /*
   * A linha densa (critica-atendimento §8): três sublinhas fixas em vez de
   * quatro — nome e hora, prévia e contador, meta —, e ~60px por linha em vez
   * de ~90. Numa tela de 1080p passam a caber treze conversas em vez de oito, e
   * a tela existe para varrer fila.
   *
   * Etapa e valor em texto neutro, não em pastilha roxa (§7): o roxo é a cor de
   * ação e de seleção, e trinta pastilhas roxas numa fila fazem a linha
   * selecionada sumir. Pastilha colorida fica só para a exceção que
   * interrompe: urgente, identidade em revisão, defasado.
   */
  const glifoDoCanal = iconeDoCanal(caixa.canal);

  return (
    <ListaLinha
      densa
      selecionada={selecionada}
      naoLida={conversa.nao_lidas > 0}
      aoAbrir={() => aoSelecionar(conversa.id)}
      inicio={
        <Avatar
          nome={cliente?.nome_exibicao}
          semente={cliente?.id}
          tamanho={36}
          destacado={selecionada}
        />
      }
      nome={
        <>
          <span className="lista-linha__nome">{nome}</span>
          {/*
            O canal como glifo ao lado do nome (v3): diz "WhatsApp" sem a
            pastilha que ficava à direita. O nome do canal continua no DOM,
            oculto, para o leitor de tela e para a busca na página; canal sem
            glifo na família (e-mail, Telegram, SMS) usa o glifo genérico.
          */}
          {nomeDoCanal(caixa.canal) && (
            <span
              className="lista-linha__canal"
              data-canal={glifoDoCanal ?? undefined}
              title={nomeDoCanal(caixa.canal) ?? undefined}
            >
              <Icone nome={glifoDoCanal ?? 'atendimento'} />
              <span className="visualmente-oculto">{nomeDoCanal(caixa.canal)}</span>
            </span>
          )}
          {conversa.prioridade === 'urgente' && <Chip tom="erro">Urgente</Chip>}
          {vinculo.estado === 'em_revisao' && (
            // O vendedor precisa saber ANTES de abrir: o painel direito vai
            // estar incompleto, e o motivo não é defeito.
            <Chip tom="atencao">Identidade em revisão</Chip>
          )}
        </>
      }
      previa={
        <>
          {conversa.ultima_mensagem?.direcao === 'saida' && (
            <span className="lista-linha__seta" aria-label="enviada por nós">
              ↩
            </span>
          )}
          <PreviaDaUltima ultima={conversa.ultima_mensagem ?? null} />
        </>
      }
      meta={
        caixa.nome?.trim() || comercial || (conversa.etiquetas ?? []).length > 0 ? (
          <>
            {/*
              A caixa de entrada (#463): com dois números do mesmo canal, o chip
              "WhatsApp" à direita não diz de qual deles a conversa veio. Nome
              longo corta com reticências, e o `title` mostra inteiro.
            */}
            {caixa.nome?.trim() && (
              <span
                className="lista-linha__meta-texto lista-linha__caixa"
                title={`Caixa de entrada: ${caixa.nome.trim()}`}
              >
                {caixa.nome.trim()}
              </span>
            )}
            {comercial?.etapa && (
              <span className="lista-linha__meta-texto">
                {nomeDaEtapa(comercial.etapa_nome ?? comercial.etapa)}
              </span>
            )}
            {valor && <span className="lista-linha__meta-texto">{valor}</span>}
            {comercial?.stale && (
              <Chip tom="stale" title="Os dados comerciais desta conversa ainda estão atualizando.">
                defasado
              </Chip>
            )}
            <Etiquetas titulos={conversa.etiquetas ?? []} limite={3} emLinha />
          </>
        ) : undefined
      }
      direita={
        <>
          {/*
            Dois carimbos: há quanto tempo a conversa existe, e quando foi a
            última mensagem.
 
            O primeiro é o que separa "cliente novo esperando" de "conversa
            velha que voltou a se mexer" — duas linhas idênticas até aqui, e com
            urgências opostas. O segundo fica na hora exata, e não em idade: a
            conversa de hoje se resolve pelo "18:31", e trocá-lo por "2h"
            perderia a precisão que se usa mais.
          */}
          {idadeCurta(conversa.aberta_em) && (
            <span className="lista-linha__idade" title="Conversa aberta há">
              {idadeCurta(conversa.aberta_em)}
            </span>
          )}
          <span className="lista-linha__quando">
            {horaCurta(conversa.ultima_mensagem?.em) ?? ''}
          </span>
          <NaoLidas quantidade={conversa.nao_lidas} />
        </>
      }
    />
  );
}

/**
 * O que a prévia diz quando a última mensagem não é texto.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * Mídia chega do motor com `content: null` — não há texto — e a lista mostrava
 * "Sem mensagens". Uma conversa que acabou de receber um áudio do cliente
 * ficava idêntica a uma conversa que nunca recebeu nada, e a única forma de
 * descobrir era abrir uma por uma.
 *
 * ── Ícone E palavra, não só o ícone ───────────────────────────────────────
 *
 * Um emoji sozinho não é lido por leitor de tela do jeito que se espera, e
 * quem usa a tela em fonte pequena confunde 🎤 com 📎. A palavra ao lado
 * resolve os dois casos e cabe: a prévia tem uma linha inteira, e mídia não
 * traz texto para disputá-la.
 *
 * ── A legenda vence a descrição ───────────────────────────────────────────
 *
 * Foto COM legenda mostra a legenda: é o que a pessoa escreveu, e é mais
 * informativo que a palavra "Imagem". O ícone continua na frente, para a linha
 * ainda dizer que veio mídia.
 *
 * ── Duração não dá ────────────────────────────────────────────────────────
 *
 * "Áudio 0:52" seria melhor, e o motor não manda duração: os campos do anexo
 * são `file_type`, `file_size`, `extension`, `width`, `height` e `thumb_url` —
 * medido no payload real em 04/09/2026. Saber a duração exigiria abrir o
 * arquivo no servidor, que é outro trabalho e outra decisão.
 */
function PreviaDaUltima({
  ultima,
}: {
  ultima: { trecho: string | null; tipo: string | null } | null;
}): ReactNode {
  if (!ultima) return <>Sem mensagens</>;

  const midia = DESCRICAO_DE_MIDIA[ultima.tipo ?? ''];
  // Sem os marcadores do WhatsApp (#615): a prévia é uma linha de texto
  // simples, cortada com reticências, e ali o asterisco só apareceria cru.
  const trecho = ultima.trecho === null ? null : textoSemMarcadores(ultima.trecho);

  if (!midia) return <>{trecho ?? 'Sem mensagens'}</>;

  return (
    <>
      <span aria-hidden="true">{midia.icone} </span>
      {trecho ?? midia.palavra}
    </>
  );
}

/**
 * O vocabulário é o do motor (`file_type`), o mesmo que a bolha já interpreta.
 *
 * Tipo desconhecido cai fora do mapa e a prévia volta ao comportamento
 * anterior — dizer o trecho, ou "Sem mensagens". É o lado seguro: um tipo novo
 * do motor aparece como sempre apareceu, em vez de virar um ícone errado.
 */
const DESCRICAO_DE_MIDIA: Record<string, { icone: string; palavra: string }> = {
  image: { icone: '📷', palavra: 'Imagem' },
  audio: { icone: '🎤', palavra: 'Áudio' },
  video: { icone: '🎬', palavra: 'Vídeo' },
  file: { icone: '📎', palavra: 'Arquivo' },
};
